import _ from "lodash";
import React, { useMemo, useCallback } from "react";
import { useQuery } from "react-query";
import { fetchSearchCeCases, fetchSearchCrmRequests, fetchSearchPermits } from "../../queries/search";
import { FilterContainer } from "./FilterContainer";
import { MultiSelect } from "../MultiSelect";
import useNetworkHeaders from "../../hooks/useNetworkHeaders";

const generateOptions = (aggregationsQuery: any) => {
  const buckets = _.keys(aggregationsQuery.data?.aggregations);
  const bucketOptions = (bucketName: string) =>
    _.get(aggregationsQuery.data, `aggregations.${bucketName}.buckets`, []).map((bucket: any) => ({
      key: bucket.key,
      value: bucket.key,
      text: bucket.key,
    }));
  return _.reduce(
    buckets,
    (result: any, bucket: string) => {
      if (_.get(aggregationsQuery.data, `aggregations.${bucket}.buckets`)) {
        result[bucket] = bucketOptions(bucket);
      } else {
        const nestedBuckets = _.keys(_.get(aggregationsQuery.data, `aggregations.${bucket}`));
        _.each(nestedBuckets, (nestedBucket) => {
          if (_.isObject(_.get(aggregationsQuery.data, `aggregations.${bucket}.${nestedBucket}`))) {
            result[`${bucket}_${nestedBucket}`] = bucketOptions(`${bucket}.${nestedBucket}`);
          }
        });
      }
      return result;
    },
    {},
  );
};

export const useCrmAggregations = (cityId: any) =>
  useAggregationFilter(cityId, fetchSearchCrmRequests, "crmList.aggregations");
export const useCodeAggregations = (cityId: any) =>
  useAggregationFilter(cityId, fetchSearchCeCases, "ceList.aggregations");
export const usePermitsAggregations = (cityId: any) =>
  useAggregationFilter(cityId, fetchSearchPermits, "permitsList.aggregations");

export const useAggregationFilter = (cityId: any, query: any, queryKey: string) => {
  const networkHeaders = useNetworkHeaders();
  const aggregationsParams = useMemo(() => ({ size: 0, cityId, aggregations: true }), [cityId]);
  const aggregationsQuery = useQuery([queryKey, aggregationsParams], query(networkHeaders), {
    enabled: !!cityId,
  });
  const options = useMemo(() => generateOptions(aggregationsQuery), [aggregationsQuery.data]);

  return {
    options,
    query: aggregationsQuery,
    data: aggregationsQuery.data,
  };
};

export const AggregationFilter = ({
  aggregations,
  name,
  filterKey,
  ...props
}: {
  aggregations: any;
  name: string;
  filterKey: string;
  placeholder?: string;
}) => {
  const render = ({ onChange, value }: any) => {
    const handler = useCallback(async (e, { value }) => onChange(value), [onChange]);
    return (
      <MultiSelect
        name={filterKey}
        value={value}
        options={aggregations.options[filterKey] || []}
        onChange={handler}
        clearable
        {...props}
      />
    );
  };
  return <FilterContainer name={name} filterKey={filterKey} render={render} />;
};
